/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Icon from "@insite/mobius/Icon";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import { priceStyles } from "@stoneage/Overrides/Widgets/ProductDetails/ProductDetailsPrice";
import { blackCheckbox } from "@stoneage/XDL/Utilities/SharedStyles/blackCheckbox";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showCompare: boolean;
    showAvailability: boolean;
    showAddToCart: boolean;
    extendedStyles?: ProductListActionsStyles;
    // XNG-Changes
    ctaHandler?: (...args: any[]) => void;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
        productSettings: getSettingsCollection(state).productSettings,
        productCompareChecked: getProductCompareChecked(state, props.productContext),
        isProductCompareFull: getIsProductCompareFull(state),
        hideProductBundleOptions: (getSettingsCollection(state) as any).customSettings?.["hideProductBundleOptions"],
        sessionCurrency: state.context.session.currency,
    };
};

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    availabilityPriceWrapper?: InjectableCss;
    availabilityWrapper?: InjectableCss;
    availabilityStyles?: ProductAvailabilityStyles;
    addToCartWrapper?: InjectableCss;
    quoteButton?: ButtonPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    priceWrapper?: InjectableCss;
    price?: ProductPriceStyles;
    compareWrapper?: InjectableCss;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
        `,
    },
    quoteButton: {
        icon: { src: "", position: "left" },
        css: css`
            width: 100%;
            justify-content: center;
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
            `,
        },
    },
    priceWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
        `,
    },
    compareWrapper: {
        css: css`
            margin-top: 11px;
            display: flex;
            justify-content: flex-end;
            > div {
                cursor: pointer;
            }
        `,
    },
    availabilityPriceWrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    ...blackCheckbox,
};

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showCompare,
    showAvailability,
    productCompareChecked,
    isProductCompareFull,
    isAuthenticated,
    extendedStyles,
    showAddToCart,
    addProductToCompare,
    removeProductFromCompare,
    hideProductBundleOptions,
    sessionCurrency,
    ctaHandler,
}) => {
    const [styles] = React.useState(() => mergeToNew(actionsStyles, extendedStyles));
    const toasterContext = React.useContext(ToasterContext);

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    const getActionButtonMessage = () => {
        const { product } = productContext;
        if (
            product.canConfigure ||
            product.isVariantParent ||
            (!!product.configurationType &&
                product.configurationType !== "None" &&
                product.configurationType !== "Fixed")
        ) {
            return translate("Configure Product");
        }

        return translate("View Product");
    };

    const { product, productInfo } = productContext;
    const productDetailPath = productContext?.productInfo?.productDetailPath;

    const isConfigurableProduct =
        !!product.configurationType && product.configurationType !== "None" && product.configurationType !== "Fixed";

    const shouldShowReplacement = product.isVariantParent || isConfigurableProduct;

    const shouldHideInventoryForBundleProducts =
        hideProductBundleOptions?.toLowerCase() === "true" &&
        product.properties["hideBundleOptions"]?.toLowerCase() === "true" &&
        isConfigurableProduct;

    const pricingRenderValue = (
        <>
            {!productInfo.pricing || productInfo.pricing?.regularPrice === 0 ? (
                <Typography {...priceStyles.replacementPrice} className="not-pdp" translate="no">
                    {sessionCurrency?.currencySymbol || sessionCurrency?.currencyCode}–.––
                </Typography>
            ) : (
                <ProductPrice
                    product={productContext}
                    showLabel={false}
                    showSavings={true}
                    showUnitOfMeasure={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.price}
                />
            )}
        </>
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && isAuthenticated && (
                <StyledWrapper {...styles.availabilityPriceWrapper}>
                    {shouldShowReplacement ? (
                        <StyledWrapper {...priceStyles.variantParentPriceReplacement} className="not-pdp">
                            {pricingRenderValue}
                            {!shouldHideInventoryForBundleProducts && (
                                <Typography {...priceStyles.replacementInventory}>
                                    <Icon src="IconGear" size={12} {...priceStyles.replacementInventoryIcon} />
                                    {siteMessage(
                                        "Custom_Inventory_ConfigureForAvailability",
                                        "Configure for Availability",
                                    )}
                                </Typography>
                            )}
                        </StyledWrapper>
                    ) : (
                        <>
                            <StyledWrapper
                                {...(isAuthenticated ? styles.priceWrapper : {})}
                                className={`${productContext.product.isVariantParent ? "variant-product" : ""}`}
                            >
                                {pricingRenderValue}
                                {!shouldHideInventoryForBundleProducts &&
                                    shouldShowReplacement &&
                                    (!productInfo.pricing || productInfo.pricing?.regularPrice === 0) &&
                                    isAuthenticated && (
                                        <Typography {...priceStyles.replacementInventory} className="not-pdp">
                                            <Icon src="IconGear" size={12} {...priceStyles.replacementInventoryIcon} />
                                            {siteMessage(
                                                "Custom_Inventory_ConfigureForAvailability",
                                                "Configure for Availability",
                                            )}
                                        </Typography>
                                    )}
                                <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                            </StyledWrapper>
                            {productSettings.showInventoryAvailability && showAvailability && (
                                <StyledWrapper {...styles.availabilityWrapper}>
                                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                                </StyledWrapper>
                            )}
                        </>
                    )}
                </StyledWrapper>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            {showAddToCart && (
                <StyledWrapper {...styles.addToCartWrapper}>
                    {/* Cart & Qty is NOT Needed For Phase 1 */}

                    {/*
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
                */}
                    <Button
                        {...styles.quoteButton}
                        href={productDetailPath}
                        onClick={() => ctaHandler?.(productContext?.product.productNumber)}
                    >
                        {getActionButtonMessage()}
                    </Button>
                </StyledWrapper>
            )}
            {showCompare && productSettings.enableProductComparisons && (
                <StyledWrapper {...styles.compareWrapper}>
                    <Checkbox
                        {...(productCompareChecked ? styles.checkBoxSelected : styles.checkBox)}
                        checked={productCompareChecked}
                        onChange={handleChanged}
                        data-test-selector="productList_CompareCheckbox"
                    >
                        {translate("Compare")}
                    </Checkbox>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions));
